import type { DateRange } from '@shared/components/FilterPopup';
import type { ExportCertificatesParams } from '../types/myCertificateTypes';

/**
 * Committed filter state of a certificate list page. Each page passes its
 * own `appliedFilters` (technician: certificateId + facilityIds;
 * client/admin: certificateId + equipmentIds + technicianIds) — absent
 * filters are simply omitted.
 */
export interface CertificateExportFilters {
  certificateId?: string;
  facilityIds?: string[];
  equipmentIds?: string[];
  technicianIds?: string[];
  dateRange: DateRange;
}

/**
 * Maps a page's committed filters to the export API params, dropping empty
 * values (blank search, empty ID arrays, blank/whitespace dates) so the
 * request carries only the filters the user actually applied.
 */
export function buildExportParams(
  filters: CertificateExportFilters
): ExportCertificatesParams {
  const start = (filters.dateRange.startDate ?? '').trim();
  const end = (filters.dateRange.endDate ?? '').trim();
  return {
    ...(filters.certificateId && { certificateId: filters.certificateId }),
    ...(filters.facilityIds && filters.facilityIds.length > 0
      ? { facilityIds: filters.facilityIds }
      : {}),
    ...(filters.equipmentIds && filters.equipmentIds.length > 0
      ? { equipmentIds: filters.equipmentIds }
      : {}),
    ...(filters.technicianIds && filters.technicianIds.length > 0
      ? { technicianIds: filters.technicianIds }
      : {}),
    ...(start && { startDate: start }),
    ...(end && { endDate: end }),
  };
}

/**
 * Number of committed filters — drives the FilterBar badge. Each ID
 * multi-select counts as one filter, each set date bound counts as one.
 * Search (`certificateId`) is deliberately not counted: it lives in the
 * search bar, not the filter popup.
 */
export function countAppliedFilters(filters: CertificateExportFilters): number {
  return [
    filters.facilityIds?.length,
    filters.equipmentIds?.length,
    filters.technicianIds?.length,
    (filters.dateRange.startDate ?? '').trim(),
    (filters.dateRange.endDate ?? '').trim(),
  ].filter(Boolean).length;
}
